import React, { useEffect, useCallback, useState, useMemo, useRef } from 'react';
import Sidebar from '../components/shared/Sidebar';
import SubmissionFilterPanel, { SubmissionFilter } from '../components/data/SubmissionFilterPanel';
import financialSubmissionService, { SubmittedFinancialData } from '../services/financialSubmissionService';
import organisationKPIsService, { OrganisationKPIs } from '../services/organisationKPIsService';
import organisationSettingsService, { OrganisationSettings } from '../services/organisationSettingsService';
import LocationGroupService from '../services/locationGroupService';
import { User } from '../types/user';
import { UserQuestion } from '../types/UserQuestion';
import useAuthStore from '../store/authStore';
import { useTheme } from '../contexts/ThemeContext';
import { useModelSelection } from '../hooks/useModelSelection';
import { CLAUDE_45_SONNET_MODEL_ID, CLAUDE_37_SONNET_MODEL_ID, NOVA_PRO_MODEL_ID } from '../utils/modelSelection';
import './AnalyticsPage.css';
import './ViewSubmissionsPage.css';
import AIAnalysisButton from '../components/csv/AIAnalysisButton';
import ReportPopup from '../components/csv/ReportPopup';
import UserQuestionService from '../services/userQuestionService';
import KPIDetailsModal from '../components/analytics/KPIDetailsModal';
import QualityCheckResultModal from '../components/analytics/QualityCheckResultModal';
import FinancialDataChatbot from '../components/chat/FinancialDataChatbot';

// Add a default placeholder for the prompt input field
const DEFAULT_PROMPT_PLACEHOLDER = "Ask a question about the submission data. For example: 'Compare submission quality scores across locations' or 'Analyze KPI trends by month'";

const QUESTION_SUGGESTIONS = [
  {
    title: "Quality Score Analysis",
    question: "Analyze the distribution of quality scores across all submissions and identify patterns"
  },
  {
    title: "Location Performance",
    question: "Compare submission quality and KPI performance across different locations"
  },
  {
    title: "Monthly Trends",
    question: "Show trends in submission quality and KPI counts over the past 6 months"
  },
  {
    title: "KPI Category Analysis", 
    question: "Break down KPI submissions by category and analyze completion rates"
  }
];

// API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Custom AI Analysis Button for Analytics page
interface CustomAIAnalysisButtonProps {
  prompt?: string;
  modelId?: string;
  organizationModelId: string; // Default model from organization settings
  disabled?: boolean;
  createCSVContent: () => Promise<{ csvContent: string; compressionLegend: any; totalKPIs: number }>;
  onAnalysisComplete: (result: string) => void;
  onAnalysisError: (error: string) => void;
  onAnalysisStart: (type: 'analysis' | 'slides') => void;
  isAnalyzing: boolean;
  buttonText?: string;
  promptType?: 'analysis' | 'slides';
  submissionFilter: SubmissionFilter;
  filteredSubmissions: SubmittedFinancialData[];
  allSubmissions: SubmittedFinancialData[];
  accessibleLocations: any[];
}

const CustomAIAnalysisButton: React.FC<CustomAIAnalysisButtonProps> = ({ 
  prompt, 
  modelId,
  organizationModelId,
  disabled,
  createCSVContent,
  onAnalysisComplete,
  onAnalysisError,
  onAnalysisStart,
  isAnalyzing,
  buttonText,
  promptType = 'analysis',
  submissionFilter,
  filteredSubmissions,
  allSubmissions,
  accessibleLocations
}) => {
  const [analysisFileRef, setAnalysisFileRef] = useState<string | null>(null);
  const { user } = useAuthStore();

  // Function to save filtered data as a file
  const saveFilteredDataAsFile = async (content: string): Promise<string> => {
    try {
      const response = await fetch(`${API_BASE_URL}/bedrock/save-filtered-data`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ content })
      });

      if (!response.ok) {
        throw new Error('Failed to save filtered data for analysis');
      }

      const data = await response.json();
      return data.fileRef;
    } catch (error) {
      console.error('Error saving filtered data:', error);
      throw new Error('Failed to save filtered data for analysis');
    }
  };

  // Function to get intelligent filter recommendations from AI
  const getIntelligentFilterRecommendations = async (
    submissions: SubmittedFinancialData[],
    userQuestion: string,
    currentDataPoints: number,
    locations: any[]
  ) => {
    try {
      // Extract metadata about available KPIs (for more granular intelligent filtering)
      const uniqueKPIs = new Set<string>();

      submissions.forEach(submission => {
        submission.kpiData.forEach(kpi => {
          uniqueKPIs.add(kpi.name);
        });
      });

      // Extract available time periods for date-aware filtering
      const availableYears = Array.from(new Set(submissions.map(s => s.year))).sort((a, b) => a - b);
      const availableMonths = Array.from(new Set(submissions.map(s => s.month))).sort((a, b) => a - b);
      const availableTimePeriods = Array.from(
        new Set(submissions.map(s => `${s.year}-${String(s.month).padStart(2, '0')}`))
      ).sort();

      const metadata = {
        totalSubmissions: submissions.length,
        currentDataPoints: currentDataPoints,
        targetDataPoints: 50000, // Increased from 20000 to allow larger datasets
        availableFilters: {
          kpiNames: Array.from(uniqueKPIs),
          years: availableYears,
          months: availableMonths,
          timePeriods: availableTimePeriods,
          dateRange: {
            earliest: availableTimePeriods[0] || 'N/A',
            latest: availableTimePeriods[availableTimePeriods.length - 1] || 'N/A'
          }
        },
        userQuestion: userQuestion
      };

      const maxAllowedKPIs = Math.floor(50000 / submissions.length); // Increased from 20000
      console.log('📤 STEP 1: Sending filter recommendation request to AI with', Array.from(uniqueKPIs).length, 'unique KPI names');
      console.log('🎯 STEP 1: Maximum KPIs AI can recommend:', maxAllowedKPIs, `(50,000 ÷ ${submissions.length} submissions)`);
      console.log('🎯 STEP 1: Sample KPIs being analyzed:', Array.from(uniqueKPIs).slice(0, 10).join(', ') + (Array.from(uniqueKPIs).length > 10 ? `... and ${Array.from(uniqueKPIs).length - 10} more` : ''));
      console.log('📅 Analytics: Available date range:', availableTimePeriods[0], 'to', availableTimePeriods[availableTimePeriods.length - 1]);

      const response = await fetch(`${API_BASE_URL}/bedrock/get-filter-recommendations`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(metadata),
      });

      if (!response.ok) {
        throw new Error('Failed to get filter recommendations from AI');
      }

      const data = await response.json();
      console.log('✅ STEP 1: Received AI filter recommendations:', data.recommendations);
      
      if (data.recommendations.kpiNames) {
        const maxKPIs = Math.floor(50000 / submissions.length); // Increased from 20000
        console.log(`🎯 STEP 1: AI recommended ${data.recommendations.kpiNames.length} KPIs (max allowed: ${maxKPIs}):`, data.recommendations.kpiNames.slice(0, 10).join(', ') + (data.recommendations.kpiNames.length > 10 ? `... and ${data.recommendations.kpiNames.length - 10} more` : ''));
        
        // Validate that AI didn't exceed the maximum
        if (data.recommendations.kpiNames.length > maxKPIs) {
          console.warn(`⚠️ STEP 1: AI exceeded maximum KPIs (${data.recommendations.kpiNames.length} > ${maxKPIs}). Truncating to first ${maxKPIs} KPIs.`);
          data.recommendations.kpiNames = data.recommendations.kpiNames.slice(0, maxKPIs);
        }
      }
      
      return data.recommendations;
    } catch (error: any) {
      console.error('❌ Error getting filter recommendations:', error);
      
      // Log more details for debugging
      if (error.message) {
        console.error('Error details:', error.message);
      }
      if (error instanceof TypeError) {
        console.error('Network or parsing error occurred');
      }
      
      return null; // Returning null will trigger the error in handleClick
    }
  };

  // Function to proceed with analysis using AI-recommended filters
  const proceedWithIntelligentFiltering = async (
    filterRecommendations: any, 
    analysisPrompt?: string, 
    analysisModelId?: string, 
    analysisPromptType?: 'analysis' | 'slides',
    locations?: any[],
    allSubmissionsToFilter?: SubmittedFinancialData[]
  ) => {
    try {
      console.log('🎯 Applying AI-recommended filters:', filterRecommendations);
      
      // Apply the recommended filters to get a new filtered dataset
      // Use all submissions if provided, otherwise fall back to filtered submissions
      let intelligentlyFilteredSubmissions = allSubmissionsToFilter || filteredSubmissions;
      
      // Use the real location name resolution
      const getLocationNameResolved = (locationId?: number) => {
        if (!locationId) return 'Organization Level';
        const location = locations?.find((loc: any) => loc.id === locationId);
        return location ? location.name : `Location ${locationId}`;
      };

      // Count unique locations before filtering
      const uniqueLocationsBefore = new Set(intelligentlyFilteredSubmissions.map(s => s.locationId)).size;
      console.log(`📍 Unique locations before filtering: ${uniqueLocationsBefore}`);

      // STEP 1: Apply DATE filtering FIRST (if AI recommended specific dates)
      if (filterRecommendations.years && filterRecommendations.years.length > 0) {
        console.log(`📅 Analytics: AI recommended filtering to years:`, filterRecommendations.years);
        
        intelligentlyFilteredSubmissions = intelligentlyFilteredSubmissions.filter(submission => 
          filterRecommendations.years.includes(submission.year)
        );
        
        console.log(`✂️ Analytics: Year filtering applied: ${intelligentlyFilteredSubmissions.length} submissions remaining`);
      }
      
      if (filterRecommendations.months && filterRecommendations.months.length > 0) {
        console.log(`📅 Analytics: AI recommended filtering to months:`, filterRecommendations.months);
        
        intelligentlyFilteredSubmissions = intelligentlyFilteredSubmissions.filter(submission => 
          filterRecommendations.months.includes(submission.month)
        );
        
        console.log(`✂️ Analytics: Month filtering applied: ${intelligentlyFilteredSubmissions.length} submissions remaining`);
      }

      // STEP 2: Apply KPI filters - either individual KPI names or categories - keep all locations and submissions
      if (filterRecommendations.kpiNames && filterRecommendations.kpiNames.length > 0) {
        console.log('🎯 Applying individual KPI name filters:', filterRecommendations.kpiNames);
        
        // Debug: Show sample KPI names from actual data
        const sampleKPINames = new Set<string>();
        intelligentlyFilteredSubmissions.slice(0, 3).forEach(submission => {
          submission.kpiData.slice(0, 5).forEach(kpi => sampleKPINames.add(kpi.name));
        });
        console.log('🔍 Debug: Sample KPI names in data:', Array.from(sampleKPINames));
        
        // Apply KPI name filtering but keep ALL submissions (even those with 0 KPIs after filtering)
        intelligentlyFilteredSubmissions = intelligentlyFilteredSubmissions
          .map(submission => ({
            ...submission,
            kpiData: submission.kpiData.filter(kpi =>
              filterRecommendations.kpiNames.includes(kpi.name)
            )
          }));
        
        // Count submissions with/without KPIs after filtering
        const submissionsWithKPIs = intelligentlyFilteredSubmissions.filter(s => s.kpiData.length > 0).length;
        const submissionsWithoutKPIs = intelligentlyFilteredSubmissions.filter(s => s.kpiData.length === 0).length;
        
        console.log(`📊 After individual KPI name filtering:`);
        console.log(`  - Submissions with KPIs: ${submissionsWithKPIs}`);
        console.log(`  - Submissions without KPIs: ${submissionsWithoutKPIs}`);
        console.log(`  - Total submissions kept: ${intelligentlyFilteredSubmissions.length}`);
        console.log(`  - KPI names kept: ${filterRecommendations.kpiNames.length}`);
        
      } else if (filterRecommendations.kpiCategories && filterRecommendations.kpiCategories.length > 0) {
        console.log('🎯 Applying KPI category filters (fallback):', filterRecommendations.kpiCategories);
        
        // Apply KPI category filtering but keep ALL submissions (even those with 0 KPIs after filtering)
        intelligentlyFilteredSubmissions = intelligentlyFilteredSubmissions
          .map(submission => ({
            ...submission,
            kpiData: submission.kpiData.filter(kpi =>
              filterRecommendations.kpiCategories.includes(kpi.category)
            )
          }));
        
        // Count submissions with/without KPIs after filtering
        const submissionsWithKPIs = intelligentlyFilteredSubmissions.filter(s => s.kpiData.length > 0).length;
        const submissionsWithoutKPIs = intelligentlyFilteredSubmissions.filter(s => s.kpiData.length === 0).length;
        
        console.log(`📊 After KPI category filtering:`);
        console.log(`  - Submissions with KPIs: ${submissionsWithKPIs}`);
        console.log(`  - Submissions without KPIs: ${submissionsWithoutKPIs}`);
        console.log(`  - Total submissions kept: ${intelligentlyFilteredSubmissions.length}`);
        
      } else {
        console.log('⚠️ No KPI recommendations received, keeping all data');
        console.log('🔍 Debug: filterRecommendations structure:', JSON.stringify(filterRecommendations, null, 2));
      }
      
      // Count unique locations after filtering
      const uniqueLocationsAfter = new Set(intelligentlyFilteredSubmissions.map(s => s.locationId)).size;
      console.log(`📍 Unique locations after KPI filtering: ${uniqueLocationsAfter}`);
      
      // Verify the filtered dataset is now within limits
      const newDataPoints = intelligentlyFilteredSubmissions.reduce((sum, submission) => sum + submission.kpiData.length, 0);
      console.log(`🎯 After intelligent filtering: ${intelligentlyFilteredSubmissions.length} submissions with ${newDataPoints.toLocaleString()} data points`);
      
      if (newDataPoints === 0) {
        throw new Error(`AI recommendations resulted in no matching data points. The recommended KPI names may not match the actual KPI names in your data.`);
      }
      
      // Removed hard limit - allow AI recommendations to proceed with large datasets
      if (newDataPoints > 100000) {
        console.warn(`⚠️ AI recommendations resulted in very large dataset: ${newDataPoints.toLocaleString()} data points - proceeding anyway`);
      }
      
      // Generate CSV content from the intelligently filtered submissions
      console.log('📊 STEP 2: Creating CSV with only AI-recommended KPIs for final analysis');
      const csvResult = await createCSVContentFromSubmissions(intelligentlyFilteredSubmissions, locations);
      
      if (!csvResult.csvContent || csvResult.csvContent.trim() === '') {
        throw new Error('No data available after intelligent filtering');
      }
      
      console.log(`✅ STEP 2: CSV created with ${csvResult.totalKPIs} KPIs (filtered from original dataset)`);
      
      // Log which KPIs are being sent to the second AI call and count CSV rows for verification
      const csvLines = csvResult.csvContent.split('\n').filter(line => line.trim() !== '');
      if (csvLines.length > 0) {
        const headers = csvLines[0].split(',');
        const kpiHeaders = headers.slice(6); // Skip Location, Year, Month, Submitted, Quality_Score, KPI_Count
        console.log('🎯 STEP 2: KPIs being sent to final AI analysis:', kpiHeaders.slice(0, 10).join(', ') + (kpiHeaders.length > 10 ? `... and ${kpiHeaders.length - 10} more` : ''));
      }

      const csvDataRows = csvLines.slice(1); // Exclude header
      const uniqueLocationsInCSV = new Set();
      
      // Parse CSV to count unique locations
      csvDataRows.forEach(row => {
        const columns = row.split(',');
        if (columns.length > 1) {
          uniqueLocationsInCSV.add(columns[1]); // Assuming location is in column 1
        }
      });

      console.log('📊 Intelligently filtered CSV data prepared:', {
        size: `${Math.round(csvResult.csvContent.length / 1024)} KB`,
        totalKPIs: csvResult.totalKPIs,
        dataPoints: newDataPoints,
        csvRows: csvDataRows.length,
        uniqueLocationsInCSV: uniqueLocationsInCSV.size
      });
      
      // Verification logging
      if (csvDataRows.length !== newDataPoints) {
        console.warn(`⚠️ CSV row count mismatch: Expected ${newDataPoints} data points, but CSV has ${csvDataRows.length} rows`);
      }
      
      console.log(`📋 CSV verification summary:`);
      console.log(`  - Expected data points: ${newDataPoints.toLocaleString()}`);
      console.log(`  - Actual CSV rows: ${csvDataRows.length.toLocaleString()}`);
      console.log(`  - Expected submissions: ${intelligentlyFilteredSubmissions.length}`);
      console.log(`  - Unique locations in data: ${uniqueLocationsAfter}`);
      console.log(`  - Unique locations in CSV: ${uniqueLocationsInCSV.size}`);

      // Additional detailed counting for verification
      const uniqueSubmissionPeriods = new Set();
      const uniqueDealers = new Set();
      
      intelligentlyFilteredSubmissions.forEach(submission => {
        // Count unique submission periods (location + year + month)
        uniqueSubmissionPeriods.add(`${submission.locationId}-${submission.year}-${submission.month}`);
        
        // Count unique dealers/locations
        uniqueDealers.add(submission.locationId);
      });

      console.log(`🏪 DEALER/LOCATION COUNT VERIFICATION:`);
      console.log(`  - Unique dealers/locations: ${uniqueDealers.size}`);
      console.log(`  - Total submissions: ${intelligentlyFilteredSubmissions.length}`);
      console.log(`  - Unique submission periods: ${uniqueSubmissionPeriods.size}`);
      console.log(`  - Average submissions per dealer: ${(intelligentlyFilteredSubmissions.length / uniqueDealers.size).toFixed(1)}`);

      // Save CSV data as file for analysis
      const fileRef = await saveFilteredDataAsFile(csvResult.csvContent);
      setAnalysisFileRef(fileRef);

      // Proceed with Bedrock analysis
      const analysisResponse = await fetch(`${API_BASE_URL}/bedrock/analyze/file`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          enableTools: true,  // Enable tool use for precise calculations
          prompt: `${analysisPrompt || 'Analyze the submission data and provide insights'}

KPI TIME PERIOD TYPES - CRITICAL INFORMATION:
The CSV column headers include time period type suffixes that indicate the data type:
- MTD (Monthly Values): Single calendar month or month-to-date data
- YTD (Year to Date): Cumulative from start of fiscal year to current date
- CYTD (Calendar Year to Date): Cumulative from January 1st to current date
- FYTD (Fiscal Year to Date): Cumulative from start of organization's fiscal year
- QTD (Quarter to Date): Cumulative from start of current quarter
- WTD (Week to Date): Accumulated from start of current week
- R3/L3M (Rolling 3 Months): Continuous 3-month period
- R6/L6M (Rolling 6 Months): Continuous 6-month period
- R12/TTM (Rolling 12 Months): Trailing twelve months
- PY (Prior Year): Same period from previous year
- PM (Prior Month): Immediately preceding month
- PQ (Prior Quarter): Immediately preceding quarter

IMPORTANT: When analyzing KPIs, ALWAYS consider the time period type:
- DO NOT directly compare MTD vs YTD values (different time scales)
- YTD/CYTD/FYTD values are cumulative and will be larger than MTD
- Rolling periods (R3/R6/R12) represent trends over time windows
- Prior period comparisons (PY/PM/PQ) are useful for growth analysis
- When calculating percentages or ratios, use values from the SAME time period type

ANALYSIS QUALITY REQUIREMENTS:
- Before making any claim about data relationships, verify it with specific examples
- If you state "locations with X above Y%", ensure ALL examples you provide actually have X above Y%
- Double-check that your examples support your conclusions
- If you find contradictory data, acknowledge it rather than ignore it

ANALYTICAL APPROACH:
1. First, examine the actual data values in your examples
2. Then, make conclusions based on what the data actually shows
3. If patterns are inconsistent, report "Mixed results observed" rather than false generalizations

CRITICAL SELF-VALIDATION - BEFORE FINALIZING YOUR RESPONSE:
- Review each claim you made
- Verify that every example you cited actually supports that claim  
- If you find any contradictions, revise your conclusions to match the actual data

When analyzing correlations:
1. STATE the relationship you're investigating (e.g., "EBITDA vs Customer Satisfaction")
2. EXAMINE specific examples with their actual values
3. CONCLUDE based only on what the examples actually show
4. QUALIFY your conclusions (e.g., "In these examples..." rather than broad generalizations)

CONTRADICTION DETECTION:
If you notice any contradiction between your statement and your examples:
- STOP and re-examine the data
- Either correct your statement or find different examples
- Never provide examples that contradict your claims`,
          fileRef: fileRef,
          modelId: analysisModelId || organizationModelId,
          requestType: analysisPromptType === 'slides' ? 'analytics_slides_generation' : 'analytics_submission_data',
          inference_config: (analysisModelId || organizationModelId) === CLAUDE_45_SONNET_MODEL_ID ? {
            max_tokens: 50000  // Limit Claude 4.5 output to 50K tokens (under 64K limit, but can still read 1M context)
          } : undefined
        }),
      });

      if (!analysisResponse.ok) {
        const errorData = await analysisResponse.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || 'Analysis request failed');
      }

      const analysisData = await analysisResponse.json();
      
      if (analysisData.result) {
        console.log('✅ Intelligent analysis completed successfully');
        onAnalysisComplete(analysisData.result);
      } else {
        throw new Error(analysisData.error || 'Analysis failed');
      }
      
    } catch (error: any) {
      console.error('❌ Intelligent filtering analysis error:', error);
      const errorMessage = error.message || 'An unexpected error occurred during intelligent analysis';
      onAnalysisError(errorMessage);
    }
  };

  // Helper function to create CSV content from a specific set of submissions
  const createCSVContentFromSubmissions = async (submissions: SubmittedFinancialData[], locations?: any[]) => {
    // Use the real location name resolution
    const getLocationNameResolved = (locationId?: number) => {
      if (!locationId) return 'Organization Level';
      const location = locations?.find((loc: any) => loc.id === locationId);
      return location ? location.name : `Location ${locationId}`;
    };
    
    const getMonthNameLocal = (month: number) => {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return months[month - 1] || month.toString();
    };

    // Fetch location groups for augmentation
    let locationGroupMap = new Map<number, string[]>();
    
    if (user?.organization_id) {
      try {
        const locationGroups = await LocationGroupService.getGroupsByOrganization(user.organization_id);
        console.log(`🏢 Analytics: Loaded ${locationGroups.length} location groups for CSV enrichment`);
        
        locationGroups.forEach(group => {
          group.location_ids.forEach((locationId: number) => {
            if (!locationGroupMap.has(locationId)) {
              locationGroupMap.set(locationId, []);
            }
            locationGroupMap.get(locationId)!.push(group.group_name);
          });
        });
        
        console.log(`🏢 Analytics: Built location group map with ${locationGroupMap.size} locations`);
      } catch (error) {
        console.error('Error loading location groups for CSV:', error);
        // Continue without group data
      }
    }

    // This is similar to the main createCSVContent but works with a specific submission set
    const allKPIs = new Set<string>();
    submissions.forEach(submission => {
      submission.kpiData.forEach(kpi => {
        // Include abbreviation to distinguish between time periods
        const abbreviation = kpi.abbreviation || 'MTD';
        const kpiId = `${kpi.category}_${kpi.name}_${abbreviation}`.replace(/[^a-zA-Z0-9_]/g, '_');
        allKPIs.add(kpiId);
      });
    });
    
    const kpiIdentifiers = Array.from(allKPIs).sort();
    console.log('📊 KPI identifiers (with abbreviations) for intelligent filtering CSV:', kpiIdentifiers.slice(0, 10));
    
    const headers = ['Location', 'Location_Groups', 'Year', 'Month', 'KPI_Count', ...kpiIdentifiers];
    
    const rows = submissions.map(submission => {
      const locationGroups = submission.locationId 
        ? locationGroupMap.get(submission.locationId) || []
        : [];
      
      const row = [
        getLocationNameResolved(submission.locationId),
        locationGroups.length > 0 ? locationGroups.join(';') : 'None',
        submission.year.toString(),
        getMonthNameLocal(submission.month),
        submission.kpiData.length.toString()
      ];

      kpiIdentifiers.forEach(kpiId => {
        const kpiValue = submission.kpiData.find(kpi => {
          // Include abbreviation to match the identifier
          const abbreviation = kpi.abbreviation || 'MTD';
          const currentKpiId = `${kpi.category}_${kpi.name}_${abbreviation}`.replace(/[^a-zA-Z0-9_]/g, '_');
          return currentKpiId === kpiId;
        });
        row.push(kpiValue && kpiValue.value !== null ? kpiValue.value.toString() : '0');
      });

      return row;
    });

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    
    // Log CSV preview for debugging
    console.log('📄 CSV Content Preview (intelligent filtering):');
    console.log('Headers:', headers.slice(0, 8).join(', ') + '...');
    console.log('First row:', rows[0]?.slice(0, 8).join(', ') + '...');
    console.log('Total rows:', rows.length);
    console.log('Total columns:', headers.length);
    
    console.log(`🏢 Analytics: CSV created with location groups - ${rows.length} rows`);
    
    return {
      csvContent,
      compressionLegend: {},
      totalKPIs: kpiIdentifiers.length
    };
  };

  const handleAnalyze = async () => {
    onAnalysisStart(promptType || 'analysis');

    try {
      console.log('🚀 Starting AI analysis of submission data...');
      
      // Always check data points threshold (regardless of intelligent filtering setting)
      const totalDataPoints = filteredSubmissions.reduce((sum, submission) => sum + submission.kpiData.length, 0);
      const totalSubmissions = filteredSubmissions.length;
      const averageKPIsPerSubmission = totalSubmissions > 0 ? Math.round(totalDataPoints / totalSubmissions) : 0;
      
      console.log(`📊 Data analysis: ${totalSubmissions} submissions with ${totalDataPoints.toLocaleString()} total data points (avg: ${averageKPIsPerSubmission} KPIs per submission)`);
      
      // Data points limit removed - allow analysis regardless of data size
      // If intelligent filtering is enabled and data is large, still offer AI recommendations
      if (totalDataPoints > 20000 && submissionFilter.intelligentFiltering) {
        console.log('🧠 Intelligent filtering enabled - offering AI filter recommendations for large dataset...');
        
        // Step 1: Get intelligent filter recommendations from AI
        // Use ALL submissions for intelligent filtering (including low quality ones)
        const allFilteredSubmissions = allSubmissions;
        const allDataPoints = allFilteredSubmissions.reduce((sum: number, submission: SubmittedFinancialData) => sum + submission.kpiData.length, 0);
        
        console.log(`🧠 Using ALL submissions for intelligent filtering: ${allFilteredSubmissions.length} submissions with ${allDataPoints.toLocaleString()} data points`);
        
        const filterRecommendations = await getIntelligentFilterRecommendations(
          allFilteredSubmissions,
          prompt || '',
          allDataPoints,
          accessibleLocations
        );
        
        if (filterRecommendations) {
          console.log('🎯 Received AI filter recommendations:', filterRecommendations);
          
          // Step 2: Apply the recommended filters and proceed with analysis
          return await proceedWithIntelligentFiltering(filterRecommendations, prompt, modelId, promptType, accessibleLocations, allFilteredSubmissions);
        } else {
          // CRITICAL FIX: Stop execution when intelligent filtering fails
          console.error('❌ AI filter recommendations failed - stopping analysis');
          throw new Error(
            '🧠 **Intelligent Filtering Failed**\n\n' +
            'The AI was unable to generate filter recommendations for your large dataset.\n\n' +
            '**What happened?**\n' +
            '• Your dataset is too large to analyze without filtering\n' +
            '• The intelligent filtering service encountered an error\n\n' +
            '**How to proceed:**\n' +
            '1. **Manual Filtering**: Use the filter panel to reduce your dataset\n' +
            '   • Select specific time periods (months/years)\n' +
            '   • Choose specific locations or regions\n' +
            '   • Focus on particular KPI categories\n\n' +
            '2. **Try Again**: The intelligent filtering service may be temporarily unavailable\n\n' +
            '3. **Disable Intelligent Filtering**: Turn it off in the filter panel and apply manual filters first\n\n' +
            'Once filtered, your analysis will proceed successfully.'
          );
        }
      }
      
      if (submissionFilter.intelligentFiltering) {
        console.log('🧠 Intelligent filtering enabled - data points within threshold, proceeding with analysis...');
      } else {
        console.log('✅ Data points within threshold, proceeding with analysis...');
      }
      
        // Generate CSV content from filtered submissions
        const csvResult = await createCSVContent();
        
        if (!csvResult.csvContent || csvResult.csvContent.trim() === '') {
          throw new Error('No data available for analysis');
        }

        // Count unique dealers and submissions for verification
        const uniqueSubmissionPeriods = new Set();
        const uniqueDealers = new Set();
        
        filteredSubmissions.forEach(submission => {
          // Count unique submission periods (location + year + month)
          uniqueSubmissionPeriods.add(`${submission.locationId}-${submission.year}-${submission.month}`);
          
          // Count unique dealers/locations
          uniqueDealers.add(submission.locationId);
        });

        console.log(`🏪 REGULAR ANALYSIS - DEALER/LOCATION COUNT VERIFICATION:`);
        console.log(`  - Unique dealers/locations: ${uniqueDealers.size}`);
        console.log(`  - Total submissions: ${filteredSubmissions.length}`);
        console.log(`  - Unique submission periods: ${uniqueSubmissionPeriods.size}`);
        console.log(`  - Average submissions per dealer: ${(filteredSubmissions.length / uniqueDealers.size).toFixed(1)}`);
        console.log(`  - Total data points: ${totalDataPoints.toLocaleString()}`);

      console.log('📊 CSV data prepared:', {
        size: `${Math.round(csvResult.csvContent.length / 1024)} KB`,
        totalKPIs: csvResult.totalKPIs
      });

      // Save CSV data as file for analysis
      const fileRef = await saveFilteredDataAsFile(csvResult.csvContent);
      setAnalysisFileRef(fileRef);

      // Prepare request body for AI analysis
        const requestBody: any = {
        enableTools: true,  // Enable tool use for precise calculations
        prompt: `${prompt || 'Analyze the submission data and provide insights'}

KPI TIME PERIOD TYPES - CRITICAL INFORMATION:
The CSV column headers include time period type suffixes that indicate the data type:
- MTD (Monthly Values): Single calendar month or month-to-date data
- YTD (Year to Date): Cumulative from start of fiscal year to current date
- CYTD (Calendar Year to Date): Cumulative from January 1st to current date
- FYTD (Fiscal Year to Date): Cumulative from start of organization's fiscal year
- QTD (Quarter to Date): Cumulative from start of current quarter
- WTD (Week to Date): Accumulated from start of current week
- R3/L3M (Rolling 3 Months): Continuous 3-month period
- R6/L6M (Rolling 6 Months): Continuous 6-month period
- R12/TTM (Rolling 12 Months): Trailing twelve months
- PY (Prior Year): Same period from previous year
- PM (Prior Month): Immediately preceding month
- PQ (Prior Quarter): Immediately preceding quarter

IMPORTANT: When analyzing KPIs, ALWAYS consider the time period type:
- DO NOT directly compare MTD vs YTD values (different time scales)
- YTD/CYTD/FYTD values are cumulative and will be larger than MTD
- Rolling periods (R3/R6/R12) represent trends over time windows
- Prior period comparisons (PY/PM/PQ) are useful for growth analysis
- When calculating percentages or ratios, use values from the SAME time period type

ANALYSIS QUALITY REQUIREMENTS:
- Before making any claim about data relationships, verify it with specific examples
- If you state "locations with X above Y%", ensure ALL examples you provide actually have X above Y%
- Double-check that your examples support your conclusions
- If you find contradictory data, acknowledge it rather than ignore it

ANALYTICAL APPROACH:
1. First, examine the actual data values in your examples
2. Then, make conclusions based on what the data actually shows
3. If patterns are inconsistent, report "Mixed results observed" rather than false generalizations

CRITICAL SELF-VALIDATION - BEFORE FINALIZING YOUR RESPONSE:
- Review each claim you made
- Verify that every example you cited actually supports that claim  
- If you find any contradictions, revise your conclusions to match the actual data

When analyzing correlations:
1. STATE the relationship you're investigating (e.g., "EBITDA vs Customer Satisfaction")
2. EXAMINE specific examples with their actual values
3. CONCLUDE based only on what the examples actually show
4. QUALIFY your conclusions (e.g., "In these examples..." rather than broad generalizations)

CONTRADICTION DETECTION:
If you notice any contradiction between your statement and your examples:
- STOP and re-examine the data
- Either correct your statement or find different examples
- Never provide examples that contradict your claims`,
        fileRef: fileRef,
        modelId: modelId || organizationModelId,
        requestType: promptType === 'slides' ? 'analytics_slides_generation' : 'analytics_submission_data'
      };

      // Add max_tokens for Claude models based on tier
      const selectedModelId = modelId || organizationModelId;
      if (selectedModelId === CLAUDE_45_SONNET_MODEL_ID) {
        // Both Premium and Premium Small use Claude 4.5, same output limit
        requestBody.inference_config = {
          max_tokens: 50000  // Limit Claude 4.5 output to 50K tokens (under 64K limit)
        };
      } else if (selectedModelId === CLAUDE_37_SONNET_MODEL_ID) {
        requestBody.inference_config = {
          max_tokens: 400000
        };
      } else if (selectedModelId === NOVA_PRO_MODEL_ID) {
        requestBody.inference_config = {
          max_tokens: 5000  // Nova Pro output limit
        };
      }

      console.log('🤖 Sending analysis request to AI...');
        
        const response = await fetch(`${API_BASE_URL}/bedrock/analyze/file`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
        throw new Error(`Analysis request failed: ${response.status}`);
        }

        const data = await response.json();
      
      if (data.result) {
        console.log('✅ AI analysis completed successfully');
        onAnalysisComplete(data.result);
        } else {
        throw new Error(data.error || 'Analysis failed');
      }
    } catch (error: any) {
      console.error('❌ Analysis error:', error);
      
      // Handle specific error types with user-friendly messages
      let errorMessage = error.message || 'An unexpected error occurred during analysis';
      
      if (error.message?.includes('Input is too long for requested model')) {
        errorMessage = '📊 **Dataset Too Large**\n\nYour dataset is too large for the AI model to process. Please try filtering your data to reduce the amount of information being analyzed:\n\n• **Time Period**: Select specific months or years\n• **Locations**: Choose specific dealerships or regions\n• **KPI Categories**: Focus on particular business areas\n• **Quality Scores**: Use the quality filter options\n\nOnce you\'ve applied filters, try your analysis again with the smaller dataset.';
      } else if (error.message?.includes('ValidationException')) {
        errorMessage = '⚠️ **AI Processing Error**\n\nThere was a validation issue with the AI model. This usually happens when the data format is unexpected or too complex to process.\n\nPlease try:\n• Filtering your data to a smaller subset\n• Using more specific analysis questions\n• Starting with a different time period';
      } else if (error.message?.includes('context limit') || error.message?.includes('token limit')) {
        errorMessage = '📈 **Data Volume Too High**\n\nThe amount of data selected exceeds what the AI can process in one analysis.\n\n**Solutions:**\n• Use the filter panel to reduce your dataset\n• Focus on specific time periods or locations\n• Try analyzing smaller chunks of data separately\n\nThis will help provide more focused and accurate insights!';
      }
      
      onAnalysisError(errorMessage);
    }
  };

  return (
      <button 
      className="analyze-button"
        onClick={handleAnalyze} 
        disabled={disabled || isAnalyzing}
      >
      {isAnalyzing ? 'Analyzing...' : (buttonText || 'Create Report')}
      </button>
  );
};

const AnalyticsPage: React.FC = () => {
  const [submissions, setSubmissions] = useState<SubmittedFinancialData[]>([]);
  const [allSubmissions, setAllSubmissions] = useState<SubmittedFinancialData[]>([]); // Store all submissions including low quality ones
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessibleLocations, setAccessibleLocations] = useState<any[]>([]);
  const [locationsLoaded, setLocationsLoaded] = useState(false);
  const [organisationSettings, setOrganisationSettings] = useState<OrganisationSettings | null>(null);

  // Modal state
  const [selectedSubmission, setSelectedSubmission] = useState<SubmittedFinancialData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [orgKPIRules, setOrgKPIRules] = useState<OrganisationKPIs[]>([]);
  
  // Quality check result modal state
  const [isQualityModalOpen, setIsQualityModalOpen] = useState(false);
  const [qualityCheckResult, setQualityCheckResult] = useState<{
    score: number;
    report: string;
    submissionId: number;
  } | null>(null);
  
  // AI Chatbot state
  const [showChatbot, setShowChatbot] = useState(false);

  // Filter state
  const [submissionFilter, setSubmissionFilter] = useState<SubmissionFilter>({
    locations: [],
    years: [],
    months: [],
    kpiCategories: [],
    includeAllQualityScores: true, // Default to include all submissions including low quality scores
    intelligentFiltering: true // Default to enabled for optimized large dataset analysis
  });
  
  // Refs to prevent duplicate API calls
  const locationsLoadingRef = useRef(false);
  const submissionsLoadingRef = useRef(false);

  // Get current user and theme
  const { user } = useAuthStore();
  const { theme } = useTheme();
  
  // Location groups state for AI Data Assistant
  const [locationGroups, setLocationGroups] = useState<any[]>([]);
  const [locationGroupsLoaded, setLocationGroupsLoaded] = useState(false);
  
  // Get organization's AI model selection
  const { modelId: organizationModelId, loading: modelLoading, aiModelTier } = useModelSelection();
  
  // Question functionality state variables
  const [userQuestion, setUserQuestion] = useState('');
  const [userQuestions, setUserQuestions] = useState<UserQuestion[]>([]);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [questionError, setQuestionError] = useState<string | null>(null);

  // Analysis state
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<string | null>(null);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [analysisType, setAnalysisType] = useState<'analysis' | 'slides'>('analysis');
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Memoized function to fetch accessible locations
  const fetchAccessibleLocations = useCallback(async () => {
    if (!user || locationsLoadingRef.current || locationsLoaded) return;
    
    console.log('🔄 Fetching accessible locations...');
    locationsLoadingRef.current = true;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/user-access/accessible-locations`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
      const data = await response.json();
        if (data.success) {
          setAccessibleLocations(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching accessible locations:', error);
      setAccessibleLocations([]);
    } finally {
      setLocationsLoaded(true);
      locationsLoadingRef.current = false;
    }
  }, [user, locationsLoaded]);

  // Memoized function to fetch organization KPI rules
  const fetchOrgKPIRules = useCallback(async () => {
    if (!user) return;
    
    try {
      console.log('🔄 Fetching organization KPI rules...');
      const rules = await organisationKPIsService.getOrganisationKPIs();
      console.log('📋 Organization KPI rules received:', rules.length);
      setOrgKPIRules(rules);
    } catch (err) {
      console.error('❌ Error fetching organization KPI rules:', err);
    }
  }, [user]);

  // Memoized function to fetch organization settings
  const fetchOrganisationSettings = useCallback(async () => {
    if (!user) return;
    
    try {
      console.log('🔄 Fetching organisation settings...');
      const settings = await organisationSettingsService.getOrganisationSettings();
      console.log('⚙️ Organisation settings received:', settings);
      setOrganisationSettings(settings);
    } catch (err) {
      console.error('❌ Error fetching organisation settings:', err);
    }
  }, [user]);

  // Memoized function to fetch submissions
  const fetchSubmissions = useCallback(async () => {
    if (!user || !locationsLoaded || submissionsLoadingRef.current) return;
    
    console.log('🔄 Fetching financial submissions...');
    submissionsLoadingRef.current = true;
    setLoading(true);
          setError(null);
          
    try {
      const data = await financialSubmissionService.getFinancialSubmissions();
      
      // First filter by accessible locations only
      const locationFilteredSubmissions = data.filter(submission => {
        // If user has no location restrictions, show all submissions for their org
        if (!submission.locationId || accessibleLocations.length === 0) {
          return true;
        }
        
        // Check if user has access to this location
        return accessibleLocations.some(loc => loc.id === submission.locationId);
      });
      
      // Store all submissions (for when includeAllQualityScores is true)
      setAllSubmissions(locationFilteredSubmissions);
      
      // Filter by quality score for default view
      const qualityFilteredSubmissions = locationFilteredSubmissions.filter(submission => {
        // Only show submissions with quality scores EQUAL TO OR ABOVE the validation lower threshold
        if (organisationSettings) {
          if (submission.dataQualityScore === null || submission.dataQualityScore === undefined) {
            return false;
          }
          if (submission.dataQualityScore < organisationSettings.validationLower) {
            return false;
        }
      } else {
          // If no organisation settings, show all submissions with quality scores
          if (submission.dataQualityScore === null || submission.dataQualityScore === undefined) {
            return false;
          }
        }
        return true;
      });
      
      setSubmissions(qualityFilteredSubmissions);
    } catch (err: any) {
      console.error('Error fetching submissions:', err);
      setError('Failed to load submitted financial data');
    } finally {
      setLoading(false);
      submissionsLoadingRef.current = false;
    }
  }, [user, locationsLoaded, accessibleLocations, organisationSettings]);

  // Fetch location groups for AI Data Assistant
  useEffect(() => {
    const fetchLocationGroups = async () => {
      if (!user?.organization_id) {
        console.log('🔍 AI Data Assistant: No organization_id, skipping location groups fetch');
        return;
      }
      
      if (locationGroupsLoaded) {
        console.log('🔍 AI Data Assistant: Location groups already loaded');
        return;
      }
      
      try {
        console.log('🏢 Analytics: Fetching location groups for AI Data Assistant...', { orgId: user.organization_id });
        const groups = await LocationGroupService.getGroupsByOrganization(user.organization_id);
        setLocationGroups(groups);
        setLocationGroupsLoaded(true);
        console.log(`🏢 Analytics: Loaded ${groups.length} location groups for AI Data Assistant`, groups);
      } catch (error) {
        console.error('❌ Error fetching location groups:', error);
        setLocationGroupsLoaded(true); // Mark as loaded even on error to prevent retries
      }
    };
    
    fetchLocationGroups();
  }, [user?.organization_id, locationGroupsLoaded]);

  // Helper functions
  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  const getLocationName = useCallback((locationId?: number) => {
    if (!locationId) return 'Organization Level';
    const location = accessibleLocations.find(loc => loc.id === locationId);
    return location ? location.name : `Location ${locationId}`;
  }, [accessibleLocations]);

  const getMonthName = useCallback((month: number) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                   'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[month - 1] || month.toString();
  }, []);

  // Apply client-side filtering to submissions
  const filteredSubmissions = useMemo(() => {
    // Use all submissions if the quality filter is enabled, otherwise use quality-filtered submissions
    let filtered = submissionFilter.includeAllQualityScores ? allSubmissions : submissions;

    // Apply location filter
    if (submissionFilter.locations.length > 0) {
      filtered = filtered.filter(submission => 
        submissionFilter.locations.includes(getLocationName(submission.locationId))
      );
    }

    // Apply year filter
    if (submissionFilter.years.length > 0) {
      filtered = filtered.filter(submission => 
        submissionFilter.years.includes(submission.year)
      );
    }

    // Apply month filter
    if (submissionFilter.months.length > 0) {
      filtered = filtered.filter(submission => 
        submissionFilter.months.includes(submission.month)
      );
    }

    // Apply KPI categories filter - filter both submissions and KPIs within submissions
    if (submissionFilter.kpiCategories.length > 0) {
      filtered = filtered
        .filter(submission => 
          submission.kpiData.some(kpi => 
            submissionFilter.kpiCategories.includes(kpi.category)
          )
        )
        .map(submission => ({
          ...submission,
          kpiData: submission.kpiData.filter(kpi => 
            submissionFilter.kpiCategories.includes(kpi.category)
          )
        }));
    }

    return filtered;
  }, [submissions, allSubmissions, submissionFilter, getLocationName]);

  // Calculate pagination
  const paginationData = useMemo(() => {
    const totalItems = filteredSubmissions.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedSubmissions = filteredSubmissions.slice(startIndex, endIndex);
      
      return {
      totalItems,
      totalPages,
      startIndex,
      endIndex,
      paginatedSubmissions,
      hasNextPage: currentPage < totalPages,
      hasPrevPage: currentPage > 1
    };
  }, [filteredSubmissions, currentPage, itemsPerPage]);

  // Transform filtered submissions into format for AI chatbot
  const aiDataFilter = useMemo(() => {
    // Build location group map
    const locationGroupMap = new Map<number, string[]>();
    
    console.log(`🔍 AI Data Assistant: Building location group map from ${locationGroups.length} groups`);
    
    locationGroups.forEach(group => {
      console.log(`🔍 Group "${group.group_name}" has ${group.location_ids?.length || 0} locations:`, group.location_ids);
      group.location_ids?.forEach((locationId: number) => {
        if (!locationGroupMap.has(locationId)) {
          locationGroupMap.set(locationId, []);
        }
        locationGroupMap.get(locationId)!.push(group.group_name);
      });
    });
    
    console.log(`🏢 Analytics: Built location group map for AI Data Assistant with ${locationGroupMap.size} locations`);
    console.log(`🏢 LocationGroupMap contents:`, Array.from(locationGroupMap.entries()).slice(0, 5));
    
    // Get sample submission location IDs
    const sampleSubmissionLocationIds = filteredSubmissions.slice(0, 5).map(s => s.locationId);
    console.log(`🔍 Sample submission location IDs:`, sampleSubmissionLocationIds);
    
    const filter = {
      isSubmissionData: true,
      years: Array.from(new Set(filteredSubmissions.map(s => s.year))),
      months: Array.from(new Set(filteredSubmissions.map(s => s.month))),
      locations: Array.from(new Set(filteredSubmissions.map(s => getLocationName(s.locationId)).filter(Boolean))),
      submissionData: filteredSubmissions.map((submission, idx) => {
        const { dataQualityReport, ...submissionWithoutReport } = submission;
        const locationGroupsForSubmission = submission.locationId 
          ? locationGroupMap.get(submission.locationId) || []
          : [];
        
        // Debug first 3 submissions
        if (idx < 3) {
          console.log(`🔍 Submission ${idx}: LocationID=${submission.locationId}, Groups=${JSON.stringify(locationGroupsForSubmission)}`);
        }
        
        return {
          ...submissionWithoutReport,
          locationName: getLocationName(submission.locationId),
          locationGroups: locationGroupsForSubmission // Add location groups
        };
      }),
      limit: filteredSubmissions.length
    };
    
    return filter;
  }, [filteredSubmissions, getLocationName, locationGroups]);

  // Modal handlers
  const handleViewSubmission = useCallback((submission: SubmittedFinancialData) => {
    setSelectedSubmission(submission);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedSubmission(null);
  }, []);

  // Filter handlers
  const handleApplyFilter = useCallback((filter: SubmissionFilter) => {
    setSubmissionFilter(filter);
    setCurrentPage(1); // Reset to first page when applying filters
  }, []);

  const handleClearFilter = useCallback(() => {
    setSubmissionFilter({
      locations: [],
      years: [],
      months: [],
      kpiCategories: [],
      includeAllQualityScores: false,
      intelligentFiltering: false
    });
    setCurrentPage(1); // Reset to first page when clearing filters
  }, []);

  // Analysis handlers
  const handleAnalysisStart = (type: 'analysis' | 'slides') => {
    setIsAnalyzing(true);
    setAnalysisResult(null);
    setIsReportOpen(false);
    setAnalysisType(type);
    setAnalysisError(null); // Clear previous errors
  };

  const handleAnalysisComplete = (result: string) => {
    setIsAnalyzing(false);
    setAnalysisResult(result);
    setIsReportOpen(true);
  };

  const handleAnalysisError = (error: string) => {
    setIsAnalyzing(false);
    setAnalysisError(error);
  };

  const openReport = () => setIsReportOpen(true);
  const closeReport = () => setIsReportOpen(false);

  // Question functionality
  const fetchUserQuestions = async () => {
    if (!user?.email) return;
    
    setIsLoadingQuestions(true);
    setQuestionError(null);
    
    try {
      const questions = await UserQuestionService.getUserQuestionsByEmail(user.email);
      setUserQuestions(questions);
    } catch (error) {
      console.error('Error fetching user questions:', error);
      setQuestionError('Failed to load your saved questions');
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  // Create CSV content from filtered submission data for AI analysis
  // Note: All null/undefined values are replaced with 0 to prevent AI calculation errors
  const createCSVContent = async () => {
    try {
      console.log('📊 Creating CSV from filtered submission data...');
      console.log('📈 Total submissions to analyze:', filteredSubmissions.length);
      
      if (filteredSubmissions.length === 0) {
        return { csvContent: '', compressionLegend: { brands: {}, dealers: {}, groups: {}, additionalFields: {} }, totalKPIs: 0 };
      }

      // Fetch location groups for augmentation
      let locationGroupMap = new Map<number, string[]>();
      
      if (user?.organization_id) {
        try {
          const locationGroups = await LocationGroupService.getGroupsByOrganization(user.organization_id);
          console.log(`🏢 Analytics: Loaded ${locationGroups.length} location groups for CSV enrichment`);
          
          locationGroups.forEach(group => {
            group.location_ids.forEach((locationId: number) => {
              if (!locationGroupMap.has(locationId)) {
                locationGroupMap.set(locationId, []);
              }
              locationGroupMap.get(locationId)!.push(group.group_name);
            });
          });
          
          console.log(`🏢 Analytics: Built location group map with ${locationGroupMap.size} locations`);
        } catch (error) {
          console.error('Error loading location groups for CSV:', error);
          // Continue without group data
        }
      }

      // Extract all unique KPI identifiers from all submissions
      // Include type abbreviation to distinguish between time periods (MTD, YTD, R3, etc.)
      const allKPIs = new Set<string>();
      filteredSubmissions.forEach(submission => {
        submission.kpiData.forEach(kpi => {
          // Use category + name + abbreviation as unique identifier
          // This allows AI to distinguish between MTD, YTD, and rolling period values
          const abbreviation = kpi.abbreviation || 'MTD'; // Default to MTD if not specified
          const kpiId = `${kpi.category}_${kpi.name}_${abbreviation}`.replace(/[^a-zA-Z0-9_]/g, '_');
          allKPIs.add(kpiId);
        });
      });
      
      const kpiIdentifiers = Array.from(allKPIs).sort();
      console.log('📋 Unique KPI identifiers found (with time period types):', kpiIdentifiers.length);

      // Create CSV headers
      const headers = [
        'location_name',
        'location_groups',
        'year',
        'month',
        'month_name',
        'total_kpis',
        ...kpiIdentifiers // Add all KPI identifiers as columns
      ];

      // Create CSV rows
      let nullReplacements = 0;
      const rows = filteredSubmissions.map(submission => {
        const locationGroups = submission.locationId 
          ? locationGroupMap.get(submission.locationId) || []
          : [];
        
        const row = [
          getLocationName(submission.locationId),
          locationGroups.length > 0 ? locationGroups.join(';') : 'None',
          submission.year.toString(),
          submission.month.toString(),
          getMonthName(submission.month),
          submission.kpiData.length.toString()
        ];

        // Add KPI values in the same order as headers
        kpiIdentifiers.forEach(kpiId => {
          const kpiValue = submission.kpiData.find(kpi => {
            // Match using category + name + abbreviation
            const abbreviation = kpi.abbreviation || 'MTD'; // Default to MTD if not specified
            const currentKpiId = `${kpi.category}_${kpi.name}_${abbreviation}`.replace(/[^a-zA-Z0-9_]/g, '_');
            return currentKpiId === kpiId;
          });
          // Use 0 for missing KPI values instead of empty string
          if (!kpiValue || kpiValue.value === null || kpiValue.value === undefined) {
            nullReplacements++;
            row.push('0');
          } else {
            row.push(kpiValue.value.toString());
          }
        });
        
        return row;
      });
      
      // Convert to CSV format
      const csvContent = [headers, ...rows]
        .map(row => row.map(cell => {
          // Escape cells that contain commas, quotes, or newlines
          if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"') || cell.includes('\n'))) {
            return `"${cell.replace(/"/g, '""')}"`;
          }
          return cell;
        }).join(','))
        .join('\n');

      console.log('✅ CSV generated successfully:', {
        totalRows: rows.length + 1, // +1 for header
        totalColumns: headers.length,
        csvSize: `${Math.round(csvContent.length / 1024)} KB`,
        nullReplacements: `${nullReplacements} null values replaced with 0`
      });

      // Log CSV preview for debugging (with abbreviations)
      console.log('📄 CSV Content Preview (main createCSVContent):');
      console.log('Headers (first 8):', headers.slice(0, 8).join(', ') + '...');
      console.log('Sample KPI headers (with abbreviations):', kpiIdentifiers.slice(0, 5));
      console.log('First data row (first 8 cols):', rows[0]?.slice(0, 8).join(', ') + '...');
      console.log('CSV first 500 chars:', csvContent.substring(0, 500));

      return { 
        csvContent, 
        compressionLegend: { brands: {}, dealers: {}, groups: {}, additionalFields: {} }, 
        totalKPIs: kpiIdentifiers.length 
      };
    } catch (error) {
      console.error('❌ Error creating CSV content from submission data:', error);
      return { csvContent: '', compressionLegend: { brands: {}, dealers: {}, groups: {}, additionalFields: {} }, totalKPIs: 0 };
    }
  };

  // Function to generate prompts based on type
  const generatePrompt = (type: 'analysis' | 'slides', question: string) => {
    if (type === 'slides') {
      return question ? `🎯 QUESTION TO ANSWER: ${question}

CRITICAL: POWERPOINT SLIDE PHYSICAL CONSTRAINTS AND SPACE CALCULATIONS

You are generating content that will be rendered in a PowerPoint presentation with EXACT physical dimensions. You must calculate whether your content fits within these constraints BEFORE generating it.

📐 SLIDE PHYSICAL SPECIFICATIONS:

SLIDE DIMENSIONS:
- Total slide size: 10.0" width × 5.625" height (16:9 aspect ratio)
- Usable slide area: 9.0" width (0.5" margins on each side)

TITLE AREA CONSTRAINTS:
- Position: x: 0.5", y: 0.3"
- Dimensions: 9.0" width × 0.8" height
- Font: 28pt, bold, Arial
- Character limit calculation:
  * At 28pt font: ~10 characters per inch
  * Maximum title width: 9.0" × 10 = 90 characters
  * Recommended: 50 characters (6 words) for readability
- MANDATORY FORMAT: "## Slide X: [Title]" (for parser - this is critical!)

CONTENT AREA CONSTRAINTS - TEXT-ONLY SLIDES:
- Position: x: 0.5", y: 1.5"
- Dimensions: 9.0" width × 4.0" height
- Starting font size: 12pt (will shrink if content overflows)
- Minimum readable font: 10pt (anything smaller is unreadable for executives)

CONTENT AREA CONSTRAINTS - SLIDES WITH CHARTS:
- Text area: 4.8" width × 4.0" height (left side)
- Chart area: 4.3" width × 4.0" height (right side)
- Text starting font: 12pt
- Chart positioned at: x: 5.5", y: 1.5"

📊 TEXT HEIGHT CALCULATIONS (You MUST use these formulas):

CHARACTER WIDTH CALCULATION:
- At 12pt font: 10 characters per inch
- At 10pt font: 12 characters per inch
- Formula: charactersPerLine = width × (10 × (12/fontSize))

Examples:
- Text-only slide at 12pt: 9.0" × 10 = 90 characters per line
- Chart slide text at 12pt: 4.8" × 10 = 48 characters per line

LINE HEIGHT CALCULATION:
- Base line height: 0.25" per line at 12pt font
- Formula: lineHeight = 0.25 × (fontSize / 12)
- Bullet spacing: 0.15" between bullets

Examples:
- 12pt font: 0.25" per line
- 10pt font: 0.208" per line
- 8pt font: 0.167" per line

TOTAL HEIGHT CALCULATION:
For each bullet point:
1. Calculate lines needed: ceil(bulletLength / charactersPerLine)
2. Calculate text height: lines × lineHeight
3. Add spacing: textHeight + 0.15"
4. Sum all bullets: totalHeight = Σ(bulletHeight + spacing) + 0.2" (bottom padding)

🎯 CONTENT LIMITS BASED ON PHYSICS:

TEXT-ONLY SLIDE (9.0" × 4.0" content area):
Available height: 4.0"
At 12pt font (0.25" per line + 0.15" spacing = 0.40" per bullet):
- Maximum bullets: 4.0" ÷ 0.40" = ~10 bullets (theoretical maximum)
- RECOMMENDED: 5 bullets maximum to keep font at 12pt
- Each bullet calculation:
  * 90 chars per line at 12pt
  * 1-line bullet: 90 chars max
  * 2-line bullet: 180 chars max (0.65" height)
  * Recommended: 80-100 characters (1 line) per bullet

CHART SLIDE (4.8" × 4.0" text area):
Available height: 4.0"
At 12pt font (0.40" per bullet including spacing):
- Maximum bullets: ~10 bullets (theoretical)
- RECOMMENDED: 3 bullets maximum to keep font at 12pt
- Each bullet calculation:
  * 48 chars per line at 12pt (MUCH NARROWER!)
  * 1-line bullet: 48 chars max
  * 2-line bullet: 96 chars max
  * Recommended: 60-70 characters per bullet

📏 CHART CONSTRAINTS:

CHART DIMENSIONS:
- Maximum width: 4.3"
- Maximum height: 4.0"
- Aspect ratio: ~1:1 (square-ish charts work best)

CHART DATA POINTS:
- At 4.3" width with labels
- Optimal: 4-6 data points (0.7-1.0" per bar)
- Maximum: 8 data points (0.5" per bar - minimum readable)
- If you have 20+ data points: Show only top 5-8

CHART COLORS (VIBRANT & COLORFUL - CRITICAL):
Use vibrant, eye-catching colors that make data pop! Examples:
- Professional: ["667eea", "f093fb", "4facfe", "43e97b", "FFA07A", "FF6B6B", "BB8FCE"]
- Classic: ["36A2EB", "FF6384", "FFCD56", "4BC0C0", "9966FF", "FF9F40", "00D9FF"]
- Bold: ["FF6B6B", "4ECDC4", "45B7D1", "FFA07A", "98D8C8", "F7DC6F", "BB8FCE"]
Note: Use hex WITHOUT # prefix (e.g., "36A2EB" not "#36A2EB")

🔴 CRITICAL: CHART-FIRST WORKFLOW (MANDATORY)

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
FOR EVERY CHART SLIDE - FOLLOW THIS EXACT SEQUENCE:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

STEP 1: CREATE CHART DATA
- Generate chart with ALL data points
- List exact values for each item
- DO NOT write bullets yet!

Example: Chart created with {North: 800, South: 950, East: 700, West: 1000}

STEP 2: ANALYZE THE CHART
- Sort: Highest to lowest
- Identify: Which is #1? Which is #2?
- Calculate: Percentages if needed

Example: Sorted [West: 1000, South: 950, North: 800, East: 700]
Highest = West (1000), Second = South (950)

STEP 3: WRITE BULLETS FROM ANALYSIS
- Use ONLY values from Step 1
- Use ONLY rankings from Step 2
- If you claim "X led", verify X is highest in Step 2

Example bullets:
- West led with 1,000 units ✅ [Verified: West IS #1]
- South followed with 950 units ✅ [Verified: South IS #2]

WRONG bullets:
- North led with 800 units ❌ [ERROR: West (1000) is higher!]
- South achieved 1,000 units ❌ [ERROR: Chart shows 950!]

STEP 4: VERIFY BEFORE FINALIZING
Q: Did I claim "X led"? → Is X actually highest in Step 2? ✓
Q: Do all values match Step 1 exactly? ✓
Q: Are all chart items mentioned in bullets OR explained why not? ✓

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
RANKING VERIFICATION (CRITICAL)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Before claiming "led/highest/strongest/topped":
1. List ALL chart values
2. Sort highest → lowest
3. Verify your claim is accurate

Example: {Store A: 400, Store B: 550, Store C: 500, Store D: 600}
Sorted: [Store D: 600, Store B: 550, Store C: 500, Store A: 400]
✅ RIGHT: "Store D led with 600" [D IS maximum]
❌ WRONG: "Store B led with 550" [D is higher!]

TIES: If multiple items share highest value:
✅ "Stores X and Y co-led with 500 each"
❌ "Store X led with 500" [Ignores Store Y]

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
SELF-CHECK QUESTIONS (Answer before submitting)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Q1: "I claimed 'Item X led' - Is X the maximum in my sorted list?"
→ If NO: STOP and fix

Q2: "Value Y for Item X - Does chart show exactly Y?"
→ If NO: STOP and correct

Q3: "Chart has 8 items, bullets mention 4 - Did I explain why?"
→ If NO: Mention all or add "Top 4..." clarification

Q4: "All percentages sum to 100%?"
→ For pie/doughnut only, must equal 100%

🧮 MANDATORY CONTENT PLANNING PROCESS:

BEFORE generating each slide, you MUST calculate:

1. **Slide Type Decision**:
   - Does this slide need a chart? → YES = 4.8" text width, NO = 9.0" text width

2. **Bullet Count Planning**:
   - Text-only: Plan for 4-5 bullets at 12pt
   - Chart slide: Plan for 2-3 bullets at 12pt

3. **Character Budget per Bullet**:
   - Text-only: 80-100 characters (90 chars = 1 line at 12pt)
   - Chart slide: 60-70 characters (48 chars = 1 line at 12pt, allow for 1.5 lines)

4. **Height Verification**:
   Text-only slide example (5 bullets):
   - Bullet 1: 85 chars = 1 line = 0.25" text + 0.15" space = 0.40"
   - Bullet 2: 92 chars = 2 lines = 0.50" text + 0.15" space = 0.65"
   - Bullet 3: 78 chars = 1 line = 0.40"
   - Bullet 4: 88 chars = 1 line = 0.40"
   - Bullet 5: 81 chars = 1 line = 0.40"
   Total: 0.40 + 0.65 + 0.40 + 0.40 + 0.40 + 0.20 (padding) = 2.45"
   Available: 4.0"
   ✅ FITS with room to spare at 12pt!

   Chart slide example (3 bullets):
   - Bullet 1: 65 chars = 2 lines (48 chars/line) = 0.50" + 0.15" = 0.65"
   - Bullet 2: 58 chars = 2 lines = 0.65"
   - Bullet 3: 62 chars = 2 lines = 0.65"
   Total: 0.65 + 0.65 + 0.65 + 0.20 = 2.15"
   Available: 4.0"
   ✅ FITS at 12pt!

⚠️ FONT SIZE DEGRADATION WARNING:

If your content exceeds available space, font will automatically shrink:
- 12pt → Readable ✅
- 10pt → Acceptable ✅
- 8pt → Hard to read ⚠️
- 6pt → Very hard to read ❌
- 4pt → Essentially unreadable ❌
- 2pt → Completely illegible ❌

**Your goal: Keep font at 10-12pt by respecting space constraints!**

🎯 MANDATORY SLIDE STRUCTURE (6-8 slides):

## Slide 1: Executive Summary
[TEXT-ONLY: 9.0" × 4.0" = Use 4-5 bullets of 80-100 chars each]
- [Bullet 1: 80-100 characters maximum]
- [Bullet 2: 80-100 characters maximum]
- [Bullet 3: 80-100 characters maximum]
- [Bullet 4: 80-100 characters maximum]
- [Bullet 5: 80-100 characters maximum]

## Slide 2: [Key Finding 1 with Chart]
[CHART SLIDE: 4.8" text × 4.0" + 4.3" chart × 4.0" = Use 2-3 bullets of 60-70 chars]
- [Bullet 1: 60-70 characters - describe chart insight]
- [Bullet 2: 60-70 characters - key takeaway]
- [Bullet 3: 60-70 characters - implication]

\`\`\`visualization
{
  "type": "bar",
  "title": "Chart Title (max 40 chars)",
  "description": "Brief description",
  "labels": ["Label1", "Label2", "Label3", "Label4", "Label5"],
  "datasets": [{
    "label": "Metric Name",
    "data": [value1, value2, value3, value4, value5],
    "backgroundColor": ["36A2EB", "FF6384", "FFCD56", "4BC0C0", "9966FF"]
  }],
  "xAxisLabel": "X Label (20 chars max)",
  "yAxisLabel": "Y Label (20 chars max)"
}
\`\`\`

## Slide 3-6: [Analysis Slides with Charts]
[Follow same format as Slide 2]

## Slide 7: Key Findings
[TEXT-ONLY: 9.0" × 4.0" = Use 4-5 bullets]

## Slide 8: Recommendations  
[TEXT-ONLY: 9.0" × 4.0" = Use 4-5 bullets]

❌ ABSOLUTE PROHIBITIONS:

1. **Title violations**:
   - ❌ Titles longer than 50 characters
   - ❌ Wrong format (MUST be "## Slide X: Title" not "## Title")

2. **Text-only slide violations**:
   - ❌ More than 5 bullets
   - ❌ Bullets longer than 100 characters
   - ❌ Paragraphs or prose

3. **Chart slide violations**:
   - ❌ More than 3 bullets in text area
   - ❌ Bullets longer than 70 characters
   - ❌ Charts with more than 8 data points
   - ❌ Dull or boring colors (use vibrant palettes!)

4. **Layout violations**:
   - ❌ More than 1 chart per slide
   - ❌ Charts on executive summary or conclusions
   - ❌ Tables, tabular data, or multi-column layouts (BREAKS FORMATTING!)
   - ❌ ASCII tables or data in row/column grid format
   - ❌ Any markdown tables | like | this | format |

5. **Data completeness violations**:
   - ❌ Missing locations from analysis
   - ❌ Incomplete data coverage

📝 USE BULLETS NOT TABLES:
❌ WRONG: | Location | Revenue |
          | Store A  | €500K   |
✅ RIGHT: • Store A: €500K revenue

✅ PRE-GENERATION CHECKLIST:

Before submitting your response, verify:
- [ ] Every slide starts with "## Slide X: [Title]"
- [ ] All titles are under 50 characters
- [ ] Text-only slides have ≤5 bullets of ≤100 chars each
- [ ] Chart slides have ≤3 bullets of ≤70 chars each
- [ ] All charts have ≤8 data points
- [ ] All charts use vibrant, colorful palettes
- [ ] NO TABLES OR TABULAR DATA anywhere (use bullets instead!)
- [ ] All locations from CSV are included in analysis
- [ ] Total slides: 6-8
- [ ] Content fits at 12pt font (use calculations above!)

📊 DATA ACCURACY REQUIREMENTS:
- Base ALL findings on actual CSV data provided
- Never invent or estimate missing data
- Use exact location names, dates, and values from CSV
- If insufficient data exists, state this limitation explicitly
- IGNORE quality_score field unless specifically asked about data quality
- Apply automotive industry financial analysis best practices
- Identify dealership performance patterns and operational inefficiencies

START YOUR RESPONSE:
## Slide 1: Executive Summary
- [First key finding: 80-100 characters]
- [Second key finding: 80-100 characters]
- [Third key finding: 80-100 characters]
- [Fourth key finding: 80-100 characters]

REMEMBER: You're working with PHYSICAL CONSTRAINTS. Calculate space before generating content!` : `CRITICAL: POWERPOINT SLIDE PHYSICAL CONSTRAINTS AND SPACE CALCULATIONS

You are generating content that will be rendered in a PowerPoint presentation with EXACT physical dimensions. You must calculate whether your content fits within these constraints BEFORE generating it.

📐 SLIDE PHYSICAL SPECIFICATIONS:

SLIDE DIMENSIONS:
- Total slide size: 10.0" width × 5.625" height (16:9 aspect ratio)
- Usable slide area: 9.0" width (0.5" margins on each side)

TITLE AREA CONSTRAINTS:
- Position: x: 0.5", y: 0.3"
- Dimensions: 9.0" width × 0.8" height
- Font: 28pt, bold, Arial
- Character limit: 50 characters (6 words) for readability
- MANDATORY FORMAT: "## Slide X: [Title]" (for parser - this is critical!)

CONTENT AREA CONSTRAINTS - TEXT-ONLY SLIDES:
- Dimensions: 9.0" width × 4.0" height
- Starting font size: 12pt
- Minimum readable font: 10pt

CONTENT AREA CONSTRAINTS - SLIDES WITH CHARTS:
- Text area: 4.8" width × 4.0" height (left side)
- Chart area: 4.3" width × 4.0" height (right side)
- Text starting font: 12pt

📊 TEXT HEIGHT CALCULATIONS:

CHARACTER WIDTH CALCULATION:
- Text-only slide at 12pt: 9.0" × 10 = 90 characters per line
- Chart slide text at 12pt: 4.8" × 10 = 48 characters per line

LINE HEIGHT CALCULATION:
- Base line height: 0.25" per line at 12pt font
- Bullet spacing: 0.15" between bullets

🎯 CONTENT LIMITS BASED ON PHYSICS:

TEXT-ONLY SLIDE (9.0" × 4.0"):
- RECOMMENDED: 5 bullets maximum at 12pt
- Each bullet: 80-100 characters (1 line)

CHART SLIDE (4.8" × 4.0" text area):
- RECOMMENDED: 3 bullets maximum at 12pt
- Each bullet: 60-70 characters

📏 CHART CONSTRAINTS:
- Optimal: 4-6 data points
- Maximum: 8 data points
- VIBRANT COLORS: ["36A2EB", "FF6384", "FFCD56", "4BC0C0", "9966FF", "FF9F40"] (no # prefix)

🔴 CRITICAL: CHART-FIRST WORKFLOW
FOR EVERY CHART: 1) Create chart data {A:500, B:450, C:600} → 2) Sort [C:600, A:500, B:450] → 3) Write bullets using sorted values → 4) Verify match
- WRONG: "B led with 450" when C (600) is higher ❌
- RIGHT: "C led with 600" when C IS maximum ✅
- WRONG: Text "A: 550" when chart shows 500 ❌
- RIGHT: Text matches chart exactly ✅

🎯 MANDATORY SLIDE STRUCTURE (6-8 slides):

## Slide 1: Executive Summary
[TEXT-ONLY: Use 4-5 bullets of 80-100 chars]
- [Bullet 1: 80-100 characters]
- [Bullet 2: 80-100 characters]
- [Bullet 3: 80-100 characters]
- [Bullet 4: 80-100 characters]

## Slide 2-6: [Analysis with Charts]
[CHART SLIDE: 2-3 bullets of 60-70 chars]
- [Bullet 1: 60-70 characters]
- [Bullet 2: 60-70 characters]

\`\`\`visualization
{
  "type": "bar",
  "title": "Chart Title (max 40 chars)",
  "description": "Brief description",
  "labels": ["L1", "L2", "L3", "L4", "L5"],
  "datasets": [{
    "label": "Metric",
    "data": [v1, v2, v3, v4, v5],
    "backgroundColor": ["36A2EB", "FF6384", "FFCD56", "4BC0C0", "9966FF"]
  }],
  "xAxisLabel": "X Label",
  "yAxisLabel": "Y Label"
}
\`\`\`

## Slide 7-8: Findings & Recommendations
[TEXT-ONLY: 4-5 bullets]

❌ PROHIBITIONS:
- ❌ Titles >50 chars or wrong format
- ❌ Text-only >5 bullets or >100 chars/bullet
- ❌ Chart slides >3 bullets or >70 chars/bullet
- ❌ Charts >8 data points or with dull colors
- ❌ Tables/tabular data (BREAKS FORMATTING! Use bullets instead)

📊 DATA REQUIREMENTS:
- Use ONLY actual CSV data
- Never invent or estimate values
- Use exact location names from CSV
- IGNORE quality_score unless asked

START: ## Slide 1: Executive Summary
- [Finding 1: 80-100 chars]
- [Finding 2: 80-100 chars]
- [Finding 3: 80-100 chars]

CALCULATE space before generating!`;
    }
    
    // Default analysis prompt (existing logic)
    const basePrompt = `${question ? `QUESTION TO ANSWER: ${userQuestion}\n\n` : ''}CRITICAL INSTRUCTIONS - READ CAREFULLY:

You are a senior financial analyst with specialized expertise in automotive accountancy and pattern recognition. You are analyzing a CSV file containing financial KPI submission data from automotive dealerships and locations. You MUST:
1. ONLY use data that exists in the provided CSV file
2. NEVER invent, estimate, or hallucinate data points
3. NEVER reference locations, dates, or values not present in the CSV
4. If data is missing or insufficient, explicitly state this limitation

ANALYSIS REQUIREMENTS:
- Base ALL findings exclusively on the provided CSV data
- Use exact location names, dates, and values from the CSV
- Apply automotive industry financial analysis best practices
- Identify dealership performance patterns and operational inefficiencies
- Recognize automotive-specific KPI trends and benchmarks
- IGNORE the quality_score field unless specifically analyzing data quality
- Focus on KPI values and business performance metrics, not data quality assessments
${question ? '- If you cannot answer the question with the available data, say so explicitly' : ''}
${!question ? `
Provide insights on:
1. Overall KPI performance patterns (from actual data only)
2. KPI performance trends across locations and time periods (from actual data only)
3. Data completeness and submission patterns (from actual data only)
4. Notable patterns or anomalies in business metrics (from actual data only)
5. Automotive industry-specific recommendations based on actual findings
6. Dealership operational efficiency insights where data supports it` : ''}

FORBIDDEN:
- Do NOT create data points not in the CSV
- Do NOT estimate missing values
- Do NOT reference locations/dates not in the file
- Do NOT make assumptions about data trends beyond what's explicitly shown
- Do NOT analyze or comment on quality_score unless specifically asked about data quality`;
    
    return basePrompt;
  };

  // Full prompt for AI analysis with strict data validation
  const fullPrompt = generatePrompt('analysis', userQuestion);
  
  // Full prompt for slides generation
  const slidesPrompt = generatePrompt('slides', userQuestion);

  // Fetch accessible locations for the user
  useEffect(() => {
    fetchAccessibleLocations();
  }, [fetchAccessibleLocations]);

  // Fetch organization KPI rules
  useEffect(() => {
    fetchOrgKPIRules();
  }, [fetchOrgKPIRules]);

  // Fetch organisation settings
  useEffect(() => {
    fetchOrganisationSettings();
  }, [fetchOrganisationSettings]);

  // Fetch submitted financial data
  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  // Fetch user questions
  useEffect(() => {
    fetchUserQuestions();
  }, [user?.email]);

  return (
    <div className="analytics-page">
      <Sidebar />
      
      <div className="analytics-content">
        <div className="page-header">
          <h1>Analytics</h1>
        </div>
        
        {error && (
          <div className="error-message">
            Error: {error}
          </div>
        )}
        
        {/* Analysis Error Banner */}
        {analysisError && (
          <div className="analysis-error-banner">
            <div className="error-content">
              <div className="error-icon">⚠️</div>
              <div className="error-message">
                <h4>Report Generation Failed</h4>
                <p style={{ whiteSpace: 'pre-wrap' }}>{analysisError}</p>
              </div>
              <button 
                className="error-close-btn"
                onClick={() => setAnalysisError(null)}
                aria-label="Close error message"
              >
                ✕
              </button>
            </div>
          </div>
        )}
        
        {/* Submission Filter Panel */}
        <div className="data-filter-section">
          <SubmissionFilterPanel
            submissions={allSubmissions.length > 0 ? allSubmissions : submissions}
            onApplyFilter={handleApplyFilter}
            onClearFilter={handleClearFilter}
            getLocationName={getLocationName}
            accessibleLocations={accessibleLocations}
          />
        </div>
        
        <div className="data-grid-section">
          {!user ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading user data...</p>
            </div>
          ) : loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading submitted financial data...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <div className="error-icon">⚠️</div>
              <h3>Error Loading Data</h3>
              <p>{error}</p>
              <button 
                className="retry-button"
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          ) : filteredSubmissions.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📊</div>
              <h3>No Submissions Found</h3>
              <p>No submitted financial data found for your accessible locations.</p>
            </div>
          ) : (
            <div className="submissions-table-container">
              <div className="table-header">
                <h3>
                  {submissionFilter.locations.length > 0 || submissionFilter.years.length > 0 || submissionFilter.months.length > 0 || submissionFilter.kpiCategories.length > 0 || submissionFilter.includeAllQualityScores
                    ? `Filtered Submissions (${filteredSubmissions.length} of ${submissionFilter.includeAllQualityScores ? allSubmissions.length : submissions.length})`
                    : `All Submissions (${filteredSubmissions.length})`
                  }
                </h3>
                {(() => {
                  const totalDataPoints = filteredSubmissions.reduce((sum, submission) => sum + submission.kpiData.length, 0);
                  const averageKPIsPerSubmission = filteredSubmissions.length > 0 ? Math.round(totalDataPoints / filteredSubmissions.length) : 0;
                  const isLargeDataset = totalDataPoints > 20000;
                  
                  // Show data points info if intelligent filtering is enabled OR if dataset is large
                  if (submissionFilter.intelligentFiltering || isLargeDataset) {
                    return (
                      <div className="data-points-info">
                        <span className="data-points-label">
                          {submissionFilter.intelligentFiltering ? '🧠 Intelligent Filtering:' : '📊 Dataset Info:'}
                        </span>
                        <span className="data-points-calculation">
                          <span className="data-points-count within-limit">
                            {filteredSubmissions.length.toLocaleString()} submissions with {totalDataPoints.toLocaleString()} data points (avg: {averageKPIsPerSubmission} KPIs/submission)
                            {isLargeDataset && !submissionFilter.intelligentFiltering && ' - Large dataset, consider enabling Intelligent Filtering for optimized analysis'}
                            {isLargeDataset && submissionFilter.intelligentFiltering && ' (Large dataset - AI will optimize if needed)'}
                          </span>
                        </span>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
              
              <div className="submissions-table">
                <div className="table-header-row">
                  <div className="table-cell header">Location</div>
                  <div className="table-cell header">Period</div>
                  <div className="table-cell header">KPIs</div>
                  <div className="table-cell header">Submitted</div>
                  <div className="table-cell header">Quality Score</div>
                  <div className="table-cell header">Actions</div>
                  </div>
                
                {paginationData.paginatedSubmissions.map((submission) => (
                  <div key={submission.id} className="table-row">
                    <div className="table-cell">
                      <div className="location-info">
                        <strong>{getLocationName(submission.locationId)}</strong>
                      </div>
                    </div>
                    
                    <div className="table-cell">
                      <div className="period-info">
                        <strong>{getMonthName(submission.month)} {submission.year}</strong>
                      </div>
                    </div>
                    
                    <div className="table-cell">
                      <div className="kpi-count">
                        <span className="count-badge">{submission.kpiData.length}</span>
                        <span className="count-label">KPIs</span>
                      </div>
                    </div>
                    
                    <div className="table-cell">
                      <div className="submitted-info">
                        <div className="submitted-date">
                          {submission.createdAt ? formatDate(submission.createdAt) : 'Unknown'}
                        </div>
                      </div>
                    </div>
                    
                    <div className="table-cell">
                      <div className="quality-score">
                        {submission.dataQualityScore !== null && submission.dataQualityScore !== undefined ? (
                          <span className={`quality-badge ${submission.dataQualityScore >= 80 ? 'good' : submission.dataQualityScore >= 60 ? 'fair' : 'poor'}`}>
                            {submission.dataQualityScore}
                          </span>
                        ) : (
                          <span className="quality-badge pending">Pending</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="table-cell">
                      <div className="action-buttons">
                    <button 
                          className="view-button"
                          onClick={() => handleViewSubmission(submission)}
                    >
                          View
                    </button>
                        {submission.dataQualityScore !== null && submission.dataQualityScore !== undefined && (
                    <button 
                            className="quality-report-button"
                            onClick={() => {
                              setQualityCheckResult({
                                score: submission.dataQualityScore!,
                                report: submission.dataQualityReport || 'No quality report available.',
                                submissionId: submission.id!
                              });
                              setIsQualityModalOpen(true);
                            }}
                          >
                            Quality Report
                    </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                  </div>
              
              {/* Pagination Controls */}
              {paginationData.totalPages > 1 && (
                <div className="pagination-container">
                  <div className="pagination-info">
                    Showing {paginationData.startIndex + 1}-{Math.min(paginationData.endIndex, paginationData.totalItems)} of {paginationData.totalItems} submissions
                  </div>
                  <div className="pagination-controls">
                    <button 
                      className="pagination-btn"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    >
                      First
                    </button>
                    <button 
                      className="pagination-btn"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={!paginationData.hasPrevPage}
                    >
                      Previous
                    </button>
                    <span className="pagination-current">
                      Page {currentPage} of {paginationData.totalPages}
                    </span>
                    <button 
                      className="pagination-btn"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={!paginationData.hasNextPage}
                    >
                      Next
                    </button>
                    <button 
                      className="pagination-btn"
                      onClick={() => setCurrentPage(paginationData.totalPages)}
                      disabled={currentPage === paginationData.totalPages}
                    >
                      Last
                    </button>
                  </div>
                </div>
              )}
                </div>
          )}
        </div>
        
        {/* Question Section */}
        <div className="question-section">
          <h3>Ask Questions About Your Data</h3>
          <p>
            Get AI-powered insights by asking specific questions about your submission data.
          </p>
          
          <div className="question-input-with-buttons">
            <div className="question-input-container">
              <textarea
                className="question-input"
                placeholder={DEFAULT_PROMPT_PLACEHOLDER}
                value={userQuestion}
                onChange={(e) => setUserQuestion(e.target.value)}
                rows={4}
              />
            </div>
            
            <div className="ai-buttons-container">
              <CustomAIAnalysisButton
                prompt={fullPrompt}
                modelId={organizationModelId}
                organizationModelId={organizationModelId}
                disabled={!userQuestion || filteredSubmissions.length === 0 || modelLoading}
                createCSVContent={createCSVContent}
                onAnalysisComplete={handleAnalysisComplete}
                onAnalysisError={handleAnalysisError}
                onAnalysisStart={handleAnalysisStart}
                isAnalyzing={isAnalyzing}
                promptType="analysis"
                submissionFilter={submissionFilter}
                filteredSubmissions={filteredSubmissions}
                allSubmissions={allSubmissions}
                accessibleLocations={accessibleLocations}
              />
              <CustomAIAnalysisButton
                prompt={slidesPrompt}
                modelId={organizationModelId}
                organizationModelId={organizationModelId}
                disabled={!userQuestion || filteredSubmissions.length === 0 || modelLoading}
                createCSVContent={createCSVContent}
                onAnalysisComplete={handleAnalysisComplete}
                onAnalysisError={handleAnalysisError}
                onAnalysisStart={handleAnalysisStart}
                isAnalyzing={isAnalyzing}
                buttonText="Create Slides"
                promptType="slides"
                submissionFilter={submissionFilter}
                filteredSubmissions={filteredSubmissions}
                allSubmissions={allSubmissions}
                accessibleLocations={accessibleLocations}
              />
            </div>
          </div>
          
          {userQuestions.length > 0 ? (
            <div className="prompt-suggestion">
                  <h4>Your Saved Questions:</h4>
                  <ul className="suggestion-list">
                {userQuestions.slice(0, 5).map((q, index) => (
                  <li key={index} onClick={() => setUserQuestion(q.questionText)}>
                    {q.questionText}
                      </li>
                    ))}
                  </ul>
            </div>
              ) : (
            <div className="prompt-suggestion">
                  <h4>Suggested Questions:</h4>
                  <ul className="suggestion-list">
                    {QUESTION_SUGGESTIONS.map((suggestion, index) => (
                      <li key={index} onClick={() => setUserQuestion(suggestion.question)}>
                        {suggestion.title}
                      </li>
                    ))}
                  </ul>
            </div>
          )}
        </div>
      </div>

      {/* Report Popup */}
      {analysisResult && (
        <>
          <ReportPopup
            isOpen={isReportOpen}
            onClose={closeReport}
            analysisResult={analysisResult}
            fileName={analysisType === 'slides' ? "Advanced Data Analytics" : "Advanced Data Analytics"}
            analysisType={analysisType}
            organizationSettings={organisationSettings}
            userQuestion={userQuestion}
          />
          {!isReportOpen && (
            <div style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 999 }}>
              <button className="primary-button" onClick={openReport}>
                View Full Report
              </button>
            </div>
          )}
        </>
      )}

      {/* KPI Details Modal */}
      {selectedSubmission && (
        <KPIDetailsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          kpiData={selectedSubmission.kpiData}
          submissionInfo={{
            location: getLocationName(selectedSubmission.locationId),
            period: `${getMonthName(selectedSubmission.month)} ${selectedSubmission.year}`,
            submittedDate: formatDate(selectedSubmission.createdAt || '')
          }}
        />
      )}

      {/* Quality Check Result Modal */}
      {qualityCheckResult && (
        <QualityCheckResultModal
          isOpen={isQualityModalOpen}
          onClose={() => {
            setIsQualityModalOpen(false);
            setQualityCheckResult(null);
          }}
          qualityScore={qualityCheckResult.score}
          qualityReport={qualityCheckResult.report}
          submissionId={qualityCheckResult.submissionId}
        />
      )}

      {/* Chatbot Toggle Button */}
      <div className="chatbot-toggle-container">
        <button 
          className="chatbot-toggle-button"
          onClick={() => setShowChatbot(!showChatbot)}
          title={showChatbot ? 'Close AI Assistant' : 'Open AI Assistant'}
        >
          <span className="chatbot-toggle-icon">💬</span>
          <span className="chatbot-toggle-text">
            {showChatbot ? 'Close AI Assistant' : 'Ask AI About Submissions'}
          </span>
          <span className="chatbot-toggle-count">
            {filteredSubmissions.length} submissions
          </span>
        </button>
      </div>

      {/* AI Data Assistant Chatbot */}
      <FinancialDataChatbot
        currentFilter={aiDataFilter}
        currentDataCount={filteredSubmissions.length}
        isVisible={showChatbot}
        onClose={() => setShowChatbot(false)}
      />
    </div>
  );
};

export default AnalyticsPage; 
